import { ClipTranscript, TranscriptSegment } from 'services/VideoEditingService';

// Captions are always grouped by sentence. The per-word groupings this used to offer
// chopped speech into fragments nobody edits by, and the choice only added a control to
// get wrong. Word-level timings are still used — see wordsWithinClip, which needs them
// to split a sentence across a cut.

export function captionDurationLabel(startSec: number, endSec: number) {
  return `(${Math.max(0, endSec - startSec).toFixed(1)}s)`;
}

// A caption remains the active on-screen phrase until the next caption begins.
// Transcript engines often end the spoken words before a short pause, so using
// only the raw end timestamp makes the label shorter than the ruler interval.
export function captionDisplayEnd(spans: TranscriptSegment[], index: number) {
  const span = spans[index];
  const next = spans[index + 1];
  return next && next.startSec > span.endSec ? next.startSec : span.endSec;
}

// Whisper's sentence-like spans, once the transcript has finished.
export function captionSpans(transcript: ClipTranscript | undefined): TranscriptSegment[] | null {
  if (!transcript || transcript.status !== 'ready') return null;
  return transcript.segments || null;
}

export interface TimelineCaptionSpan {
  left: number;
  width: number;
  text: string;
  durationLabel: string;
}

// Project source-time transcript spans onto one trimmed timeline clip. Video and
// audio actions share this so both lanes use exactly the same caption cadence.
// Whether a clip's caption strip has anything worth the space. A transcript that
// FINISHED and produced no spans over the clip's trimmed range is silent footage —
// or speech too faint to survive transcription — and saying "no speech detected"
// under every such clip just fills the timeline with noise. A transcript still
// running or failed does have something to report, so it keeps its strip.
export function hasCaptionsWorthShowing(
  transcript: ClipTranscript | undefined,
  sourceInSec: number,
  sourceOutSec: number,
): boolean {
  const spans = timelineCaptionSpans(transcript, sourceInSec, sourceOutSec);
  if (spans && spans.length > 0) return true;
  return transcript?.status !== 'ready';
}

// The words of one caption that actually fall inside a clip's trimmed range.
//
// Needed because a caption is only whole from the SOURCE file's point of view. Split a
// clip mid-sentence and that sentence overlaps both halves, so both used to print it in
// full — the same words appearing twice, each on a clip that only contains part of them.
// Rebuilding from the word timings gives each half exactly the words spoken in it.
//
// Returns null when there is nothing to rebuild FROM, in which case the caller keeps the
// caption whole — better one that overreaches than one invented from nothing. That covers
// three cases: the caption sits wholly inside the range (the common case, no work to do);
// the transcript predates word-level timestamps; or the word list, though present, holds
// no words for this caption at all.
//
// That last case has to be told apart from "this caption's words are all outside the
// clip", which returns an empty array and legitimately drops the caption. Both look like
// zero words after filtering, but only one of them means the clip is silent here — a
// transcription gap is missing DATA, and dropping the line would hide speech the strip
// should still show.
function wordsWithinClip(
  span: { startSec: number; endSec: number },
  words: TranscriptSegment[] | undefined,
  sourceInSec: number,
  sourceOutSec: number,
): TranscriptSegment[] | null {
  if (span.startSec >= sourceInSec && span.endSec <= sourceOutSec) return null;
  if (!words || words.length === 0) return null;
  // Words belonging to this caption, whether or not the clip contains them.
  const ofCaption = words.filter((word) => word.startSec < span.endSec && word.endSec > span.startSec);
  if (ofCaption.length === 0) return null;
  // …narrowed to the ones actually spoken inside the clip.
  return ofCaption.filter((word) => word.endSec > sourceInSec && word.startSec < sourceOutSec);
}

export function timelineCaptionSpans(
  transcript: ClipTranscript | undefined,
  sourceInSec: number,
  sourceOutSec: number,
): TimelineCaptionSpan[] | null {
  const grouped = captionSpans(transcript);
  const duration = sourceOutSec - sourceInSec;
  if (!grouped || duration <= 0) return null;
  return grouped
    .map((span, index) => ({ ...span, displayEndSec: captionDisplayEnd(grouped, index) }))
    .filter((span) => span.displayEndSec > sourceInSec && span.startSec < sourceOutSec)
    .map((span) => {
      const retained = wordsWithinClip(span, transcript?.words, sourceInSec, sourceOutSec);
      // A caption clipped down to no words at all is not this clip's speech — drop it
      // rather than draw an empty block.
      if (retained && retained.length === 0) return null;
      // When the text was rebuilt, bound the block by the words that survived rather
      // than by the original caption clamped to the edge: the point of rebuilding is
      // that the block and its words describe the same moment.
      const from = retained
        ? Math.max(retained[0].startSec, sourceInSec)
        : Math.max(span.startSec, sourceInSec);
      const to = retained
        ? Math.min(retained[retained.length - 1].endSec, sourceOutSec)
        : Math.min(span.displayEndSec, sourceOutSec);
      return {
        left: ((from - sourceInSec) / duration) * 100,
        width: Math.max(((to - from) / duration) * 100, 1.5),
        text: retained ? retained.map((word) => word.text).join(' ') : span.text,
        durationLabel: captionDurationLabel(from, to),
      };
    })
    .filter((span): span is TimelineCaptionSpan => span !== null);
}
